import { faWandMagicSparkles } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import styled from 'styled-components'

const WEAK_MATCH_THRESHOLD = 0.3

const Banner = styled.div<{ $isWeakMatch: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 0.95rem 1rem;
  margin: 0 1rem 0.75rem;
  border-radius: 18px;
  border: 1px solid
    ${({ $isWeakMatch, theme }) =>
      $isWeakMatch ? 'rgba(255, 105, 0, 0.22)' : 'rgba(12, 35, 64, 0.08)'};
  background:
    radial-gradient(circle at top right, rgba(255, 105, 0, 0.12), transparent 42%),
    linear-gradient(180deg, rgba(255, 255, 255, 0.98) 0%, rgba(245, 247, 250, 0.98) 100%);
  box-shadow: 0 16px 36px rgba(12, 35, 64, 0.08);

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}px) {
    flex-direction: column;
    align-items: flex-start;
  }
`

const BannerContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;

  h4,
  p {
    margin: 0;
  }

  h4 {
    color: ${({ theme }) => theme.colors.primary};
    font-size: 0.98rem;
  }

  p {
    color: ${({ theme }) => theme.colors.darkGrey};
    font-size: 0.9rem;
    line-height: 1.35;
  }
`

const ScoreBadge = styled.span<{ $isWeakMatch: boolean }>`
  display: inline-flex;
  align-items: center;
  align-self: flex-start;
  padding: 0.28rem 0.65rem;
  border-radius: 999px;
  background: ${({ $isWeakMatch, theme }) =>
    $isWeakMatch ? 'rgba(255, 105, 0, 0.14)' : 'rgba(12, 35, 64, 0.08)'};
  color: ${({ $isWeakMatch, theme }) =>
    $isWeakMatch ? theme.colors.secondary : theme.colors.primary};
  font-size: 0.76rem;
  font-weight: 700;
  letter-spacing: 0.03em;
  text-transform: uppercase;
`

const AskButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.55rem;
  min-width: 210px;
  padding: 0.8rem 1rem;
  border: none;
  border-radius: 999px;
  background: linear-gradient(
    135deg,
    ${({ theme }) => theme.colors.primary} 0%,
    #1f4f8b 48%,
    ${({ theme }) => theme.colors.secondary} 100%
  );
  color: ${({ theme }) => theme.colors.lightText};
  font-size: 0.92rem;
  font-weight: 700;
  cursor: pointer;
  box-shadow: 0 14px 24px rgba(12, 35, 64, 0.18);
  transition:
    transform 180ms ease,
    opacity 180ms ease,
    box-shadow 180ms ease;

  &:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: 0 18px 28px rgba(12, 35, 64, 0.22);
  }

  &:disabled {
    opacity: 0.72;
    cursor: wait;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}px) {
    width: 100%;
  }
`

interface VariableSearchMatchBannerProps {
  bestMatchScore: number
  question: string
  onAsk: (question: string) => void | Promise<void>
  isLoading?: boolean
}

export default function VariableSearchMatchBanner({
  bestMatchScore,
  question,
  onAsk,
  isLoading = false,
}: VariableSearchMatchBannerProps) {
  const isWeakMatch = bestMatchScore <= WEAK_MATCH_THRESHOLD

  if (!isWeakMatch) {
    return null
  }

  return (
    <Banner $isWeakMatch={isWeakMatch}>
      <BannerContent>
        <ScoreBadge $isWeakMatch={isWeakMatch}>
          Busca aproximada
        </ScoreBadge>
        <h4>Os resultados ainda parecem aproximados</h4>
        <p>
          Os itens abaixo podem ajudar, mas a Prix IA pode encontrar uma variável mais certeira
          para a sua intenção.
        </p>
      </BannerContent>

      <AskButton type='button' onClick={() => void onAsk(question)} disabled={isLoading}>
        <FontAwesomeIcon icon={faWandMagicSparkles} />
        {isLoading ? 'Abrindo Prix IA...' : 'Perguntar para a IA'}
      </AskButton>
    </Banner>
  )
}
